import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from "axios";
import Layout from '../components/Layout';
import { API_URL } from "../config"; // ajustează calea
import { getClientSocket, connectClientSocket } from '../socket';


import { File, FileImage, Download } from "lucide-react";
import { ImageDown, FileDown } from "lucide-react";

interface FileItem {
  name: string;
  url: string;
  timestamp?: string;
}

interface ImageItem {
  imageName: string;
  url: string;
  timestamp?: string;
}

interface ChatMessage {
  id: number;
  orderId: number;
  sender: string;
  messageType: string;
  content: string;
  fileName?: string | null;
  fileUrl?: string | null;
  timestamp: string;
}

interface Order {
  id: number;
  orderNo: string;
  orderRef: string;
  jobType: string;
  estimatedDate: string;
  shippedBy: string;
  message: string;
  description?: string;
  imageName?: string;
  status: string;
  trackingNo: string;
  orderDate: string;
  shippedDate: string;
  files?: FileItem[];
  images?: ImageItem[];
  messages?: ChatMessage[];
}

interface AttachedFile {
  name: string;
  url: string;
  type: 'initial' | 'chat';
  category: 'image' | 'document';
  timestamp: Date;
}

interface MessageHistoryData {
  type: string;
  messages: ChatMessage[];
  files?: any[];
}

interface NewMessageData {
  type: string;
  message: ChatMessage;
}

interface FileUploadData {
  type: string;
  file: any;
}

const OrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  // State pentru chat
  const [messageInput, setMessageInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // State pentru chat fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);

  const token = localStorage.getItem("token");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Functie pentru a determina categoria fișierului
  const getFileCategory = (fileName: string): 'image' | 'document' => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension) ? 'image' : 'document';
  };

  // Funcție pentru descărcarea fișierului
  const handleDownload = (fileName: string) => {
    const downloadUrl = `${API_URL}/download/${fileName}`;
    window.open(downloadUrl, '_blank');
  };




  useEffect(() => {
  connectClientSocket();
}, []);



  // Fetch order data
  useEffect(() => {
    if (!token) {
      alert('You must be logged in');
      navigate('/auth');
      return;
    }

    const fetchOrder = async () => {
      try {
        console.log('Fetching order data for ID:', id);
        const res = await axios.get(`${API_URL}/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('Order data received:', res.data);
        const data = res.data as Order;
        setOrder(data);

        // Setează mesajele din datele inițiale
        if (data.messages && data.messages.length > 0) {
          console.log('Setting initial messages:', data.messages);
          setMessages(data.messages);
        }

        // ADAUGĂ ACEST COD AICI - marchează notificările ca citite
        const markAsRead = async () => {
          try {
            await axios.put(`${API_URL}/orders/${id}/mark-read`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Notificări marcate ca citite pentru order:', id);
          } catch (error) {
            console.error('Error marking notifications as read:', error);
          }
        };

        // ✅ FIX: Marchează ca citit după ce Socket s-a conectat
        if (id) {
          setTimeout(() => {
            markAsRead();
          }, 2000); // Așteaptă 2 secunde pentru Socket connection
        }

      } catch (error: any) {
        console.error("Error fetching order:", error);
        if (error.response?.status === 404) {
          alert('Order not found');
          navigate('/my-orders');
        } else {
          alert("Failed to fetch order data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, navigate, token]);

  // Funcție separată pentru reîncărcarea datelor
  const fetchOrderData = async () => {
    try {
      const res = await axios.get(`${API_URL}/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data) {
        const data = res.data as Order;
        setOrder(data);
      }
    } catch (error) {
      console.error("Error refetching order:", error);
    }
  };

  // Socket.IO connection pentru chat
useEffect(() => {
  if (!id) return;

    const socket = getClientSocket();
  if (!socket) return;

  console.log('Joining order room:', id);
  socket.emit('joinOrder', Number(id));

  const handleConnect = () => {
    console.log('Socket.IO connected in OrderDetails');
    setIsConnected(true);
  };

  const handleDisconnect = () => {
    console.log('Socket.IO disconnected in OrderDetails');
    setIsConnected(false);
  };

  const handleMessageHistory = (data: MessageHistoryData) => {
    console.log('Message history received in OrderDetails:', data);
    if (data.messages && data.messages.length > 0) {
      setMessages(prevMessages => {
        const existingIds = new Set(prevMessages.map(msg => msg.id));
        const newMessages = data.messages.filter(msg => !existingIds.has(msg.id));
        const allMessages = [...prevMessages, ...newMessages];

        return allMessages.sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
    }
  };

  const handleNewMessage = (data: NewMessageData) => {
    console.log('🔔 New message received in OrderDetails:', data);
    console.log('📤 Message sender value:', data.message?.sender);
    if (data.message) {
      setMessages(prev => {
        // Check if message already exists by ID
        if (prev.some(msg => msg.id === data.message.id)) {
          console.log('⚠️ Message already exists with ID:', data.message.id);
          return prev;
        }
        // Match temporary message with server message (for optimistic update)
        const matchIdx = prev.findIndex(msg => {
          const matches = (
            msg.id < 0 &&
            msg.sender === data.message.sender &&
            msg.content === data.message.content &&
            Math.abs(new Date(msg.timestamp).getTime() - new Date(data.message.timestamp).getTime()) < 10000
          );
          if (matches) {
            console.log('✅ Found matching temp message:', {tempId: msg.id, sender: msg.sender, content: msg.content.substring(0, 30)});
          }
          return matches;
        });
        if (matchIdx !== -1) {
          // Replace temporary message with server message
          console.log('🔄 Replacing temp message at index', matchIdx);
          const copy = [...prev];
          copy[matchIdx] = data.message;
          return copy.sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        }
        // Add new message if no match found
        console.log('📌 No match found, adding as new message');
        const updatedMessages = [...prev, data.message];
        return updatedMessages.sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
    }
  };

  const handleOrderUpdated = (updatedOrder: Partial<Order>) => {
    setOrder(prev => {
      if (!prev) return prev;
      return { ...prev, ...updatedOrder };
    });

    setTimeout(() => {
      fetchOrderData();
    }, 1000);
  };

  const handleFileUploaded = (data: FileUploadData) => {
    console.log('File uploaded event received:', data);
    setTimeout(() => {
      fetchOrderData();
    }, 1000);
  };

  const handleMessageDeleted = (data: any) => {
    console.log('Message deleted received in OrderDetails:', data);
    setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
  };

  const handleMessageEdited = (data: any) => {
    console.log('Message edited received in OrderDetails:', data);
    setMessages(prev => prev.map(msg => 
      msg.id === data.messageId 
        ? { ...msg, content: data.newContent }
        : msg
    ));
  };

  // TOATE LISTENERS O SINGURĂ DATĂ
  socket.on('connect', handleConnect);
  socket.on('disconnect', handleDisconnect);
  socket.on('messageHistory', handleMessageHistory);
  socket.on('newMessage', handleNewMessage);
  socket.on('orderUpdated', handleOrderUpdated);
  socket.on('fileUploaded', handleFileUploaded);
  socket.on('newFile', handleFileUploaded);
  socket.on('messageDeleted', handleMessageDeleted);
  socket.on('messageEdited', handleMessageEdited);

  if (socket.connected) {
    setIsConnected(true);
  }

  // UN SINGUR RETURN LA SFÂRȘIT
  return () => {
    socket.off('connect', handleConnect);
    socket.off('disconnect', handleDisconnect);
    socket.off('messageHistory', handleMessageHistory);
    socket.off('newMessage', handleNewMessage);
    socket.off('orderUpdated', handleOrderUpdated);
    socket.off('fileUploaded', handleFileUploaded);
    socket.off('newFile', handleFileUploaded);
    socket.off('messageDeleted', handleMessageDeleted);
    socket.off('messageEdited', handleMessageEdited);
  };
}, [id, token]);

  // Auto-scroll DOAR pentru chat container, nu pentru toată pagina
  useEffect(() => {
    const chatContainer = document.getElementById('chat-messages');
    if (chatContainer) {
      const isNearBottom = chatContainer.scrollTop + chatContainer.clientHeight >= chatContainer.scrollHeight - 100;

      // Scroll doar dacă user-ul e aproape de jos în chat
      if (isNearBottom || messages.length <= 1) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Toggle fullscreen pentru chat
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // În handleSendMessage din OrderDetails.tsx
const handleSendMessage = async (e?: React.FormEvent) => {
  e?.preventDefault();


  const socket = getClientSocket();
  if (!socket) {
    alert('Chat not connected');
    return;
  }

  if (!messageInput.trim() && files.length === 0 && images.length === 0) return;

  setSending(true);

  try {
    // Dacă avem doar text, trimite prin Socket.IO
    if (messageInput.trim() && files.length === 0 && images.length === 0) {
      console.log('📤 Sending message from Client via Socket.IO:', messageInput.trim());
      
      // Add temporary message so client sees it immediately (optimistic update)
      const tempId = -Date.now();
      const tempMsg = {
        id: tempId,
        orderId: Number(id),
        sender: 'client',
        messageType: 'text',
        content: messageInput.trim(),
        timestamp: new Date().toISOString()
      } as ChatMessage;
      console.log('📝 Created temp message:', {id: tempMsg.id, sender: tempMsg.sender, content: tempMsg.content.substring(0, 30)});
      setMessages(prev => [...prev, tempMsg]);
      
      socket.emit('sendMessage', {
        orderId: Number(id),
        sender: 'client',
        content: messageInput.trim()
      });
      console.log('✉️ Emitted sendMessage event with sender: Client');
      setMessageInput('');
    } else {
      // Dacă avem fișiere, trimite prin HTTP
      const formData = new FormData();

      if (messageInput.trim()) {
        formData.append('content', messageInput.trim());
      }

      // Adaugă fișierele
      files.forEach(file => {
        formData.append('files', file);
        console.log('Adding file to FormData:', file.name);
      });
      
      images.forEach(image => {
        formData.append('images', image);
        console.log('Adding image to FormData:', image.name);
      });

      console.log('Sending message with files via HTTP');
      console.log('Files count:', files.length);
      console.log('Images count:', images.length);

      const res = await axios.post(`${API_URL}/orders/${id}/message`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      console.log('✅ Message with files sent successfully:', res.data);

      // Emite evenimente socket pentru fișiere încărcate
      files.forEach(file => {
        socket.emit('fileUploaded', {
          orderId: Number(id),
          fileName: file.name,
          fileUrl: `${API_URL}/uploads/${file.name}`,
          uploadedBy: 'Client',
          type: 'file'
        });
      });

      images.forEach(image => {
        socket.emit('fileUploaded', {
          orderId: Number(id),
          fileName: image.name,
          fileUrl: `${API_URL}/uploads/${image.name}`,
          uploadedBy: 'Client',
          type: 'image'
        });
      });

      // Curăță formele
      setFiles([]);
      setImages([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (imageInputRef.current) imageInputRef.current.value = '';
      setMessageInput('');

      // Reîncarcă datele după 2 secunde
      setTimeout(() => {
        fetchOrderData();
      }, 2000);
    }
  } catch (error) {
    console.error('❌ Error sending message:', error);
    alert('Error sending message');
  } finally {
    setSending(false);
  }
};

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (cents: number | null | undefined) => {
    if (!cents || typeof cents !== 'number') return "N/A";
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Funcție pentru a obține toate fișierele combinate și separate pe categorii
  const getAllAttachedFiles = () => {
    const allFiles: AttachedFile[] = [];

    // Adaug fișierul inițial de la creare (dacă există)
    if (order?.imageName) {
      allFiles.push({
        name: order.imageName,
        url: `${API_URL}/api/download/${order.imageName}`,
        type: 'initial',
        category: getFileCategory(order.imageName),
        timestamp: new Date(order.orderDate)
      });
    }

    // Adaug fișierele din chat (din order.files și order.images)
    if (order?.files) {
      order.files.forEach(file => {
        allFiles.push({
          name: file.name,
          url: file.url.startsWith('http') ? file.url : `${API_URL}${file.url}`,
          type: 'chat',
          category: getFileCategory(file.name),
          timestamp: file.timestamp ? new Date(file.timestamp) : new Date()
        });
      });
    }

    if (order?.images) {
      order.images.forEach(image => {
        allFiles.push({
          name: image.imageName,
          url: image.url.startsWith('http') ? image.url : `${API_URL}${image.url}`,
          type: 'chat',
          category: 'image',
          timestamp: image.timestamp ? new Date(image.timestamp) : new Date()
        });
      });
    }

    // Sortez după timestamp
    const sortedFiles = allFiles.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Separare pe categorii
    const images = sortedFiles.filter(file => file.category === 'image');
    const documents = sortedFiles.filter(file => file.category === 'document');

    return { images, documents };
  };

  const renderMessage = (msg: ChatMessage) => {
    const isClient = msg.sender === 'client';

    return (
      <div
        key={msg.id}
        className={`flex ${isClient ? 'justify-end' : 'justify-start'} mb-3`}
      >
        <div
          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${isClient
            ? 'bg-green-600 text-white'
            : 'bg-green-500 text-white'
            }`}
        >
          <div className="text-xs mb-1 opacity-75">
            {isClient ? 'You' : 'felixdent'} • {formatDate(msg.timestamp)}
          </div>

          {msg.messageType === 'text' && (
            <div className="break-words">{msg.content}</div>
          )}

          {msg.messageType === 'file' && msg.fileName && (
            <div>
              <div className="break-words mb-1">{msg.content}</div>
              <button
                onClick={() => handleDownload(msg.fileName!)}
                className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-100 underline cursor-pointer"
              >
                📎 {msg.fileName}
              </button>
            </div>
          )}

          {msg.messageType === 'image' && msg.fileName && (
            <div>
              <div className="break-words mb-2">{msg.content}</div>
              <img
                src={msg.fileUrl || ''}
                alt={msg.fileName || 'Uploaded image'}
                className="max-w-[300px] rounded cursor-pointer hover:opacity-80"
                onClick={() => handleDownload(msg.fileName!)}
                title={`Click to download: ${msg.fileName}`}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFileTable = () => {
    const { images, documents } = getAllAttachedFiles();

    if (images.length === 0 && documents.length === 0) {
      return (
        <div className="mt-6 p-4 border rounded bg-green-700 dark:bg-neutral-500 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-200">
            Uploaded Files & Images
          </h3>
          <p className="text-gray-500 dark:text-gray-400">No files uploaded yet</p>
        </div>
      );
    }

    return (
      <div className="mt-6 p-4 border rounded bg-green-200 dark:bg-neutral-500 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-200">
          Uploaded Files & Images
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Files Column */}
          {documents.length > 0 && (
            <div>
              <h4 className="font-medium mb-3 text-gray-700 dark:text-gray-300 border-b pb-2">
                Documents ({documents.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {documents.map((file, index) => (
                  <div key={`doc-${index}`} className="flex items-center justify-between p-2 border rounded hover:bg-neutral-600 dark:hover:bg-neutral-700">
                    <div className="flex items-center">
                        <File className="w-5 h-5 text-green-500 mr-2" />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{file.name}</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {file.type === 'initial' ? 'Initial upload' : 'Chat attachment'} • {formatDate(file.timestamp)}
                        </div>
                      </div>
                    </div>
                    <button
  onClick={() => handleDownload(file.name)}
  className="p-1 hover:text-gray-700 text-gray-500 transition-colors"
  title="Download file"
>
  <Download className="w-5 h-5 text-green-500" />
</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images Column */}
          {images.length > 0 && (
            <div>
              <h4 className="font-medium mb-3 text-gray-700 dark:text-gray-300 border-b pb-2">
                Images ({images.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {images.map((image, index) => (
                  <div key={`img-${index}`} className="flex items-center justify-between p-2 border rounded hover:bg-neutral-600 dark:hover:bg-neutral-700">
                    <div className="flex items-center">
                      <FileImage className="w-5 h-5 text-green-500 mr-2" />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 break-words">{image.name}</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {image.type === 'initial' ? 'Initial upload' : 'Chat attachment'} • {formatDate(image.timestamp)}
                        </div>
                      </div>
                    </div>
                    <button
  onClick={() => handleDownload(image.name)}
  className="p-1 hover:text-gray-700 text-gray-500 transition-colors"
  title="Download image"
>
  <Download className="w-5 h-5 text-green-500" />
</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#025951]"></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">Order Not Found</h1>
            <button
              onClick={() => navigate('/my-orders')}
              className="px-6 py-2 bg-[#025951] text-white rounded-lg hover:bg-[#034a43] transition-colors"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen text-gray-900 dark:text-white">
        <div className="max-w-6xl mx-auto p-4 md:p-6">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Order Details</h1>
              <p className="text-gray-600 dark:text-gray-400">Order #{order.orderNo}</p>
            </div>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-600 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm">Chat Connected</span>
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <div className="w-2 h-2 bg-red-600 rounded-full mr-2"></div>
                  <span className="text-sm">Chat Disconnected</span>
                </div>
              )}

            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Order Information */}
            <div className="bg-green-200 dark:bg-neutral-600 rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Order Information</h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order No</label>
                    <div className="text-lg font-bold text-[#025951] dark:text-[#047066]">{order.orderNo}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${order.status === 'Created' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' :
                      order.status === 'Received' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                        order.status === 'In process' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100' :
                          order.status === 'In pending' ? 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100' :
                            order.status === 'Dispatched' ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                      }`}>
                      {order.status}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Reference</label>
                    <div className="text-gray-900 dark:text-gray-100">{order.orderRef}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Job Type</label>
                    <div className="text-gray-900 dark:text-gray-100">{order.jobType || 'N/A'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Date</label>
                    <div className="text-gray-900 dark:text-gray-100">{formatDate(order.orderDate)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estimated Date</label>
                    <div className="text-gray-900 dark:text-gray-100">{formatDate(order.estimatedDate)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tracking No</label>
                    <div className="text-gray-900 dark:text-gray-100">{order.trackingNo || 'Not assigned'}</div>
                  </div>

                </div>

                {order.shippedBy && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shipped By</label>
                    <div className="text-gray-900 dark:text-gray-100">{order.shippedBy}</div>
                  </div>
                )}

                {order.shippedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shipped Date</label>
                    <div className="text-gray-900 dark:text-gray-100">{formatDate(order.shippedDate)}</div>
                  </div>
                )}

                {order.message && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Initial Message</label>
                    <div className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-600 p-3 rounded">{order.message}</div>
                  </div>
                )}

                {order.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                    <div className="text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-neutral-600 p-3 rounded">{order.description}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Section */}
            <div className="bg-green-500 dark:bg-neutral-600 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Chat with Felixdent</h2>
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-xl hover:text-2xl transition-all"
                  title="Toggle fullscreen"
                >
                  ⤢
                </button>
              </div>

              {/* Messages Container */}
              <div
                id="chat-messages"
                className={`border rounded-lg ${isFullscreen ? 'h-96' : 'h-64'} overflow-y-auto p-4 mb-4 bg-gray-50 dark:bg-neutral-700`}
              >
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                    <p>No messages yet. Start a conversation!</p>
                  </div>
                ) : (
                  <>
                    {messages.map(renderMessage)}
                    <div ref={chatEndRef} />
                  </>
                )}
              </div>

              {/* Message Input Form */}
              <form onSubmit={handleSendMessage} className="space-y-3">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message here... (Shift+Enter for new line, Enter to send)"
                  className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg resize-none focus:ring-2 focus:ring-[#025951] focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  rows={3}
                />

                {/* File Upload Inputs */}
<div className="flex items-center gap-4">

  {/* Hidden File Input */}
  <input
    ref={fileInputRef}
    type="file"
    multiple
    onChange={(e) => setFiles(Array.from(e.target.files || []))}
    className="hidden"
  />

  {/* Hidden Image Input */}
  <input
    ref={imageInputRef}
    type="file"
    multiple
    accept="image/*"
    onChange={(e) => setImages(Array.from(e.target.files || []))}
    className="hidden"
  />

  {/* File Icon Button */}
  <button
    type="button"
    onClick={() => fileInputRef.current?.click()}
    className="p-3 rounded-full bg-neutral-100 hover:bg-neutral-200 hover:scale-110 transition-all duration-200"
    title="Attach Files"
  >
    <FileDown className="w-5 h-5 text-[#025951]" />
  </button>

  {/* Image Icon Button */}
  <button
    type="button"
    onClick={() => imageInputRef.current?.click()}
    className="p-3 rounded-full bg-neutral-100 hover:bg-neutral-200 hover:scale-110 transition-all duration-200"
    title="Attach Images"
  >
    <ImageDown className="w-5 h-5 text-[#025951]" />
  </button>

</div>

{/* Selected Files Display */}
{(files.length > 0 || images.length > 0) && (
  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
    {files.length > 0 && <span>{files.length} file(s) selected</span>}
    {files.length > 0 && images.length > 0 && <span>, </span>}
    {images.length > 0 && <span>{images.length} image(s) selected</span>}
  </div>
)}

                {/* Selected Files Display */}
                {(files.length > 0 || images.length > 0) && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {files.length > 0 && <span>{files.length} file(s) selected</span>}
                    {files.length > 0 && images.length > 0 && <span>, </span>}
                    {images.length > 0 && <span>{images.length} image(s) selected</span>}
                  </div>
                )}

                {/* Send Button */}
                <button
                  type="submit"
                  disabled={sending || (!messageInput.trim() && files.length === 0 && images.length === 0)}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${sending || (!messageInput.trim() && files.length === 0 && images.length === 0)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#025951] text-white hover:bg-[#034a43] focus:ring-2 focus:ring-[#025951] focus:ring-opacity-50'
                    }`}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          {/* Files Section */}
          {renderFileTable()}

          {/* Back Button la sfârșit */}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => navigate('/my-orders')}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-green transition-colors"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetails;